import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Popconfirm, Space, Table, Tabs, Tooltip } from 'antd';
import { toast } from 'react-toastify';
import { collect } from 'collect.js';
import { DeleteOutlined, PictureOutlined, TagsOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import useRouteMatch from '../../../hooks/navigation/useRouteMatch';
import useApiClient from '../../../hooks/api/useApiClient';
import { SMART_PRICING_MODIFIER_TYPE, SMART_PRICING_RESOURCE_TYPE } from '../../../services/exports/Constants';
import HelperMethods from '../../../services/exports/HelperMethods';
import useHelpers from '../../../hooks/context/useHelpers';
import { Stringable } from '../../../services/helpers/Stringable';
import SmartPricingRuleModal from '../../../modals/smart-pricing/SmartPricingRuleModal';
import SmartPricingRuleImagesModal from '../../../modals/marketing/SmartPricingRuleImagesModal';
import Theme from '../../../styles/Theme';
import usePermissions, { ManagerPermission } from '../../../hooks/auth/usePermissions';
import { ScreenEnum } from '../../../hooks/navigation/Screens';
import { selectCurrentFranchiseId } from '../../../store/slices/CurrentFranchiseSlice';

export const menus_tab = SMART_PRICING_RESOURCE_TYPE.menu;
export const categories_tab = SMART_PRICING_RESOURCE_TYPE.category;
export const products_tab = SMART_PRICING_RESOURCE_TYPE.product;
export const modifier_groups_tab = SMART_PRICING_RESOURCE_TYPE.modifier_group;
export const modifiers_tab = SMART_PRICING_RESOURCE_TYPE.modifier;

export default function SmartPricingView() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Menu:SmartPricing' });
  const isMasterRoute = !!useRouteMatch(ScreenEnum.MasterSmartPricing);
  const globalTab = isMasterRoute ? SMART_PRICING_RESOURCE_TYPE.franchise : SMART_PRICING_RESOURCE_TYPE.company;
  const { SmartPricingRulesManager } = useApiClient();
  const franchiseId = useSelector(selectCurrentFranchiseId);
  const company = useSelector((state) => state.currentCompany.company);
  const { formatCurrency } = useHelpers();
  const { can } = usePermissions();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(globalTab);

  const [showRuleModal, setShowRuleModal] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [selectedRule, setSelectedRule] = useState(null);
  const [showImagesModal, setShowImagesModal] = useState(false);
  const [selectedRuleForImages, setSelectedRuleForImages] = useState(null);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [deleting, setDeleting] = useState(false);

  const tabs = collect([globalTab])
    .when(can(ManagerPermission.CreateMenuSmartPricingRules), (collection) => collection.push(menus_tab))
    .merge([categories_tab, products_tab, modifier_groups_tab, modifiers_tab])
    .toArray();

  const tabData = useMemo(() => collect(data).where('resource_type', currentTab).toArray(), [data, currentTab]);

  useEffect(() => {
    loadData();
  }, [isMasterRoute]);

  async function loadData() {
    setLoading(true);
    const { success, data: responseData } = await SmartPricingRulesManager.get(
      isMasterRoute && { is_master: isMasterRoute },
    );
    setLoading(false);

    if (!success) {
      toast.error(t('toasts.failed_to_load_data'));

      return;
    }

    setData(responseData);
  }

  const clearSelection = () => setSelectedRowKeys([]);

  async function deleteRules() {
    if (!selectedRowKeys.length) return;

    setDeleting(true);
    const { success } = await SmartPricingRulesManager.bulkDelete({
      ids: selectedRowKeys,
      resource_type: currentTab,
      ...(isMasterRoute && { is_master: isMasterRoute }),
    });
    setDeleting(false);

    if (!success) {
      toast.error(t('toasts.failed_to_delete'));

      return;
    }

    setData((current) =>
      collect(current ?? [])
        .whereNotIn('id', selectedRowKeys)
        .toArray(),
    );
    clearSelection();
    toast.success(t('toasts.deleted_successfully'));
  }

  const openRuleModal = (rule = null) => {
    const IDS_MAP = {
      [SMART_PRICING_RESOURCE_TYPE.company]: company?.id,
      [SMART_PRICING_RESOURCE_TYPE.franchise]: franchiseId ?? company?.franchise_id,
    };

    setSelectedRule(rule);
    setSelectedResource({
      id: IDS_MAP[currentTab] ?? null,
      ...(rule?.resource ?? {}),
      type: currentTab,
    });

    setShowRuleModal(true);
  };

  const closeRuleModal = () => {
    setShowRuleModal(false);
    setSelectedRule(null);
    setSelectedResource(null);
  };

  const onRuleSave = (responseData) => {
    if (selectedRule) {
      setData((current) =>
        collect(current ?? [])
          .put(
            collect(current ?? []).search((item) => item.id === selectedRule.id),
            responseData,
          )
          .toArray(),
      );

      closeRuleModal();

      return;
    }

    setData((current) =>
      collect(current ?? [])
        .push(responseData)
        .toArray(),
    );
    closeRuleModal();
  };

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: (keys) => setSelectedRowKeys(keys),
      getCheckboxProps: (record) => ({ disabled: record.resource_type !== currentTab }),
    }),
    [selectedRowKeys, currentTab],
  );

  const renderTab = (name) => (
    <div className="mt-4 bg-background-inkWhite-white_0 shadow rounded-lg">
      <div className="flex justify-between items-center gap-4 p-md border-b border-brand-inkGrey-grey_2">
        <h5 className="!my-auto">{t(`tabs.${name}`)}</h5>

        <div className="flex items-center gap-2">
          {selectedRowKeys.length > 0 && (
            <Popconfirm
              title={t('popups.delete_rule.title')}
              icon={<DeleteOutlined />}
              onConfirm={deleteRules}
              okButtonProps={{
                danger: true,
                loading: deleting,
              }}
            >
              <Button danger>{t('buttons.delete', { count: selectedRowKeys.length })}</Button>
            </Popconfirm>
          )}

          <Button type="primary" onClick={() => openRuleModal()}>
            {t('buttons.add_rule')}
          </Button>
        </div>
      </div>

      <Table
        rowSelection={rowSelection}
        columns={[
          {
            key: 'name',
            title: t('fields.name'),
            render: (_, item) => (
              <>
                {item.resource.name}
                {item.is_visible && (
                  <Tooltip title={item.name} className="ml-xs" color={Theme.color.companyBrand.primary}>
                    <TagsOutlined className="text-md text-brand-primary" />
                  </Tooltip>
                )}
              </>
            ),
          },
          {
            key: 'order_method',
            title: t('fields.order_methods'),
            render: (_, item) => collect(item.order_methods).implode(', '),
          },
          {
            key: 'modifier',
            title: t('fields.modifier'),
            render: (_, item) =>
              ({
                [SMART_PRICING_MODIFIER_TYPE.absolute]: `${t('labels.absolute')} ${formatCurrency(item.modifier_value)}`,
                [SMART_PRICING_MODIFIER_TYPE.fixed_value]: new Stringable(
                  HelperMethods.formatCurrency(item.modifier_value),
                )
                  .when(item.modifier_value > 0, (stringable) => stringable.prepend('+'))
                  .toString(),
                [SMART_PRICING_MODIFIER_TYPE.percentage]: new Stringable(
                  HelperMethods.formatPercentage(item.modifier_value),
                )
                  .when(item.modifier_value > 0, (stringable) => stringable.prepend('+'))
                  .toString(),
              })[item.modifier_type],
          },
          {
            key: 'image_url',
            title: t('fields.image'),
            dataIndex: 'image_url',
            render: (image_url) =>
              image_url ? (
                <img src={image_url} alt="rule" className="h-8 w-auto rounded object-cover border border-gray-200" />
              ) : (
                <span className="text-brand-text-grey">—</span>
              ),
          },
          {
            key: 'actions',
            title: t('fields.actions'),
            render: (_, item) => (
              <Space size="middle">
                <a onClick={() => openRuleModal(item)} className="!text-[#1677ff]">
                  {t('buttons.edit')}
                </a>
                {item.is_visible && (
                  <Tooltip title={t('buttons.images')}>
                    <Button
                      size="small"
                      icon={<PictureOutlined />}
                      onClick={() => {
                        setSelectedRuleForImages(item);
                        setShowImagesModal(true);
                      }}
                    />
                  </Tooltip>
                )}
              </Space>
            ),
          },
        ]}
        dataSource={tabData}
        loading={loading}
        pagination={false}
        rowKey="id"
      />
    </div>
  );

  return (
    <>
      <h2 className="mb-lg">{t(isMasterRoute ? 'master_title' : 'title')}</h2>

      <Alert
        type="info"
        className="mb-lg"
        title={t('sections.how_it_works.title')}
        description={t('sections.how_it_works.description')}
        showIcon
      />

      <Tabs
        activeKey={currentTab}
        onChange={(key) => {
          setCurrentTab(key);
          clearSelection();
        }}
        items={tabs.map((item) => ({
          key: item,
          label: t(`tabs.${item}`),
          children: renderTab(item),
        }))}
      />

      {showRuleModal && (
        <SmartPricingRuleModal
          open
          resource={selectedResource}
          data={selectedRule}
          onSuccess={onRuleSave}
          onCancel={closeRuleModal}
          isMaster={isMasterRoute}
        />
      )}
      {showImagesModal && (
        <SmartPricingRuleImagesModal
          rule={selectedRuleForImages}
          open={showImagesModal}
          onChange={(nextImageUrl) => {
            setSelectedRuleForImages((prev) => (prev ? { ...prev, image_url: nextImageUrl } : prev));
            setData((prev) =>
              prev.map((row) => (row.id === selectedRuleForImages?.id ? { ...row, image_url: nextImageUrl } : row)),
            );
          }}
          onClose={() => {
            setShowImagesModal(false);
            setSelectedRuleForImages(null);
          }}
        />
      )}
    </>
  );
}
